"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.alpha = alpha;
exports.contrastRatio = contrastRatio;
exports.flattenOver = flattenOver;
exports.parseColor = parseColor;
exports.relativeLuminance = relativeLuminance;
/**
 * Small color utilities used by the token system and its contrast tests.
 * Pure TS — no platform dependencies.
 */

/** Parses '#RGB', '#RRGGBB', '#RRGGBBAA', 'rgb(...)' and 'rgba(...)'. */
function parseColor(color) {
  const hex = color.trim();
  if (hex.startsWith('#')) {
    const body = hex.slice(1);
    if (body.length === 3) {
      const [r, g, b] = body.split('').map(c => parseInt(c + c, 16));
      return {
        r,
        g,
        b,
        a: 1
      };
    }
    if (body.length === 6 || body.length === 8) {
      const r = parseInt(body.slice(0, 2), 16);
      const g = parseInt(body.slice(2, 4), 16);
      const b = parseInt(body.slice(4, 6), 16);
      const a = body.length === 8 ? parseInt(body.slice(6, 8), 16) / 255 : 1;
      return {
        r,
        g,
        b,
        a
      };
    }
    throw new Error(`Unsupported hex color: ${color}`);
  }
  const match = hex.match(/^rgba?\(([^)]+)\)$/i);
  if (match) {
    const parts = match[1].split(',').map(p => parseFloat(p.trim()));
    const [r, g, b, a = 1] = parts;
    return {
      r,
      g,
      b,
      a
    };
  }
  throw new Error(`Unsupported color format: ${color}`);
}

/** Returns an rgba() string of `color` at the given opacity (0–1). */
function alpha(color, opacity) {
  const {
    r,
    g,
    b
  } = parseColor(color);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/** Alpha-composites `foreground` over an opaque `background`. */
function flattenOver(foreground, background) {
  const fg = parseColor(foreground);
  const bg = parseColor(background);
  const a = fg.a + bg.a * (1 - fg.a);
  const blend = (f, b) => Math.round((f * fg.a + b * bg.a * (1 - fg.a)) / a);
  return {
    r: blend(fg.r, bg.r),
    g: blend(fg.g, bg.g),
    b: blend(fg.b, bg.b),
    a
  };
}
function channelLuminance(value) {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** WCAG relative luminance of an opaque color. */
function relativeLuminance(color) {
  return 0.2126 * channelLuminance(color.r) + 0.7152 * channelLuminance(color.g) + 0.0722 * channelLuminance(color.b);
}

/**
 * WCAG contrast ratio between `foreground` and an opaque `background`.
 * Translucent foregrounds are composited over the background first.
 */
function contrastRatio(foreground, background) {
  const fg = flattenOver(foreground, background);
  const bg = parseColor(background);
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const [lighter, darker] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (lighter + 0.05) / (darker + 0.05);
}